import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, formatCurrency, formatNumber, statusColors } from '@/lib/utils';
import type {
  KPISummary,
  ChannelPerformance,
  AdPacingRow,
  AdCostTrendRow,
  AdKeywordCostRow,
} from '@/types';
import { api } from '@/lib/api';
import { SocialInsightsStrip } from '@/components/statistics/SocialInsightsStrip';
import { SocialPerformanceCard } from '@/components/statistics/SocialPerformanceCard';

const CHART_COLORS = [
  '#2563eb', // primary-600
  '#3b82f6', // primary-500
  '#60a5fa', // primary-400
  '#1d4ed8', // primary-700
  '#1e40af', // primary-800
  '#93c5fd', // primary-300
];

const EMPTY_KPI: KPISummary = {
  total_leads: 0, total_conversions: 0, total_revenue: 0, roi: 0, roas: 0,
  cost_per_lead: 0, conversion_rate: 0, hours_invested: 0,
};

export function StatisticsPage({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(new Date().getFullYear());
  const [compareYear, setCompareYear] = useState<number | null>(null);
  const [kpiCompare, setKpiCompare] = useState<KPISummary | null>(null);
  const [planId, setPlanId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpi, setKpi] = useState<KPISummary>({
    total_leads: 0,
    total_conversions: 0,
    total_revenue: 0,
    roi: 0,
    roas: 0,
    cost_per_lead: 0,
    conversion_rate: 0,
    hours_invested: 0,
  });
  const [budgetByChannel, setBudgetByChannel] = useState<{ channel: string; planned: number; actual: number }[]>([]);
  const [revenueTimeline, setRevenueTimeline] = useState<{ month: string; revenue: number }[]>([]);
  const [channelPerf, setChannelPerf] = useState<(ChannelPerformance & { type: string })[]>([]);
  const [topCampaigns, setTopCampaigns] = useState<{ name: string; roi: number; roas?: number; revenue: number; cost: number }[]>([]);
  const [planOptions, setPlanOptions] = useState<{ id: string; title: string }[]>([]);
  const [adPacing, setAdPacing] = useState<AdPacingRow[]>([]);
  const [adCostTrend, setAdCostTrend] = useState<AdCostTrendRow[]>([]);
  const [adKeywordCosts, setAdKeywordCosts] = useState<AdKeywordCostRow[]>([]);
  const [syncingAdData, setSyncingAdData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.get<{ data: { id: string; title: string }[] }>(`/marketing-plans?year=${year}&limit=100`),
      api.get<{ data: KPISummary }>(`/statistics/kpi?year=${year}${planId ? `&plan_id=${planId}` : ''}`),
      api.get<{ data: { by_channel: { channel: string; planned: number; actual: number }[] } }>(`/statistics/budget?year=${year}${planId ? `&plan_id=${planId}` : ''}`),
      api.get<{ data: { month: string; revenue: number }[] }>(`/statistics/revenue?year=${year}${planId ? `&plan_id=${planId}` : ''}`),
      api.get<{ data: ChannelPerformance[] }>(`/statistics/channels?year=${year}${planId ? `&plan_id=${planId}` : ''}`),
      api.get<{ data: { name: string; roi: number; roas?: number; revenue: number; cost: number }[] }>(`/statistics/campaigns?year=${year}${planId ? `&plan_id=${planId}` : ''}&limit=5`),
      api.get<{ data: AdPacingRow[] }>(`/statistics/ad-pacing?year=${year}${planId ? `&plan_id=${planId}` : ''}&platform=all`),
      api.get<{ data: AdCostTrendRow[] }>(`/statistics/ad-cost-trend?days=30${planId ? `&plan_id=${planId}` : ''}&platform=all`),
      api.get<{ data: AdKeywordCostRow[] }>(`/statistics/ad-keywords?days=30&limit=12${planId ? `&plan_id=${planId}` : ''}&platform=google_ads`),
    ])
      .then(([plansRes, kpiRes, budgetRes, revenueRes, channelsRes, campaignsRes, adPacingRes, adCostTrendRes, adKeywordsRes]) => {
        if (cancelled) return;
        const plans = (plansRes as { data?: { id: string; title: string }[] }).data ?? [];
        setPlanOptions(Array.isArray(plans) ? plans : []);
        setKpi((kpiRes as { data?: KPISummary }).data ?? {
          total_leads: 0,
          total_conversions: 0,
          total_revenue: 0,
          roi: 0,
          roas: 0,
          cost_per_lead: 0,
          conversion_rate: 0,
          hours_invested: 0,
        });
        const budgetData = (budgetRes as { data?: { by_channel: { channel: string; planned: number; actual: number }[] } }).data;
        setBudgetByChannel(budgetData?.by_channel ?? []);
        setRevenueTimeline((revenueRes as { data?: { month: string; revenue: number }[] }).data ?? []);
        const ch = (channelsRes as { data?: ChannelPerformance[] }).data ?? [];
        setChannelPerf(ch.map((c) => ({ ...c, type: (c as ChannelPerformance & { type?: string }).type ?? c.channel })));
        setTopCampaigns((campaignsRes as { data?: { name: string; roi: number; roas?: number; revenue: number; cost: number }[] }).data ?? []);
        setAdPacing((adPacingRes as { data?: AdPacingRow[] }).data ?? []);
        setAdCostTrend((adCostTrendRes as { data?: AdCostTrendRow[] }).data ?? []);
        setAdKeywordCosts((adKeywordsRes as { data?: AdKeywordCostRow[] }).data ?? []);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message ?? 'Daten konnten nicht geladen werden.');
          setKpi(EMPTY_KPI);
          setBudgetByChannel([]);
          setRevenueTimeline([]);
          setChannelPerf([]);
          setTopCampaigns([]);
          setAdPacing([]);
          setAdCostTrend([]);
          setAdKeywordCosts([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [year, planId]);

  useEffect(() => {
    setKpiCompare(null);
    if (compareYear == null) {
      return;
    }
    let cancelled = false;
    void api
      .get<{ data: KPISummary }>(`/statistics/kpi?year=${compareYear}${planId ? `&plan_id=${planId}` : ''}`)
      .then((res) => {
        if (cancelled) return;
        setKpiCompare((res as { data?: KPISummary }).data ?? null);
      })
      .catch(() => {
        if (!cancelled) setKpiCompare(null);
      });
    return () => {
      cancelled = true;
    };
  }, [compareYear, planId]);

  const triggerAdSync = async () => {
    try {
      setSyncingAdData(true);
      await api.post('/integrations/sync/ad-spend');
    } catch (e) {
      setError((e as Error).message || 'Ad-Spend-Sync konnte nicht gestartet werden.');
    } finally {
      setSyncingAdData(false);
    }
  };

  if (loading && !kpi.total_leads) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
          <p className="text-sm text-muted">Statistiken werden geladen…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SocialPerformanceCard onNavigate={onNavigate} />
      <SocialInsightsStrip onNavigate={onNavigate} />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Statistiken</h1>
          <p className="text-sm leading-relaxed text-muted">
            Kennzahlen zu Leads, Budget und Kanälen — gefiltert nach Jahr und Marketingplan. Die Qualität hängt von gepflegten
            Ist-Werten in Aktivitäten und Kampagnen ab. Ad-Sync holt externe Daten nach, sofern Integrationen aktiv sind.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-3">
          <Button variant="outline" onClick={triggerAdSync} disabled={syncingAdData}>
            {syncingAdData ? 'Sync startet…' : 'Ad-Daten jetzt synchronisieren'}
          </Button>
          <Select value={String(year)} onValueChange={(v) => { setYear(Number(v)); setPlanId(''); setCompareYear(null); }}>
            <SelectTrigger className="w-[120px]" aria-label="Statistikjahr">
              <SelectValue placeholder="Jahr" />
            </SelectTrigger>
            <SelectContent>
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={planId || 'all'} onValueChange={(v) => setPlanId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px]" aria-label="Marketingplan">
              <SelectValue placeholder="Plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Pläne</SelectItem>
              {planOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={compareYear == null ? 'none' : String(compareYear)}
            onValueChange={(v) => setCompareYear(v === 'none' ? null : Number(v))}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Jahresvergleich" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein KPI-Vergleich</SelectItem>
              {[year - 1, year - 2, year - 3].map((y) => (
                <SelectItem key={y} value={String(y)}>
                  Vergleich mit {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {adPacing.length === 0 ? (
          <Card className="lg:col-span-3 border-border bg-card shadow-sm">
            <CardContent className="py-8 text-sm text-muted">
              Noch keine Ad-Spend-Daten vorhanden. Starte den Sync und hinterlege Google/LinkedIn Integrationen.
            </CardContent>
          </Card>
        ) : (
          adPacing.map((row) => (
            <Card key={row.channel} className="border-border bg-card shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{row.channel === 'google_ads' ? 'Google Ads' : 'LinkedIn Ads'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted">Budget Ziel</span><span>{formatCurrency(row.target)}</span></div>
                <div className="flex justify-between"><span className="text-muted">Ist Kosten</span><span>{formatCurrency(row.actual)}</span></div>
                <div className="flex justify-between"><span className="text-muted">Soll bis heute</span><span>{formatCurrency(row.expected_to_date)}</span></div>
                <div className="flex justify-between items-center">
                  <span className="text-muted">Pacing</span>
                  <Badge variant={row.pacing_percent > 110 || row.pacing_percent < 90 ? 'secondary' : 'success'}>
                    {row.pacing_percent.toFixed(1)}%
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {error && (
        <div className={cn('rounded-lg border px-4 py-3 text-sm', 'border-warning/30 bg-warning-soft text-warning')}>
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
        {[
          { label: 'Leads gesamt', value: formatNumber(kpi.total_leads), sub: 'Anzahl' },
          { label: 'Conversions', value: formatNumber(kpi.total_conversions), sub: `${kpi.conversion_rate}% Rate` },
          { label: 'Umsatz', value: formatCurrency(kpi.total_revenue), sub: 'Total' },
          { label: 'ROI', value: `${kpi.roi.toFixed(1)}x`, sub: 'Return on Investment' },
          { label: 'ROAS', value: `${(kpi.roas ?? 0).toFixed(1)}x`, sub: 'Return on Ad Spend' },
          { label: 'Kosten pro Lead', value: formatCurrency(kpi.cost_per_lead), sub: 'CHF' },
          { label: 'Stunden investiert', value: formatNumber(kpi.hours_invested), sub: 'h' },
        ].map((item) => (
          <Card key={item.label} className="border-border bg-card shadow-sm">
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-medium text-muted">{item.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-bold text-foreground">{item.value}</p>
              <p className="text-xs text-muted">{item.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {compareYear != null && kpiCompare && (
        <Card className="border-border bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">{`KPI-Vergleich: ${year} vs. ${compareYear}`}</CardTitle>
            <p className="text-sm text-muted">
              Gleicher Planfilter wie oben. Nur Kernzahlen — vergleicht aggregierte Ist-Werte.
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted">
                    <th className="py-2 pr-4 font-medium">Kennzahl</th>
                    <th className="py-2 pr-4 font-medium tabular-nums">{year}</th>
                    <th className="py-2 pr-4 font-medium tabular-nums">{compareYear}</th>
                    <th className="py-2 font-medium">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { k: 'Leads gesamt', cur: kpi.total_leads, prev: kpiCompare.total_leads },
                    { k: 'Umsatz (CHF)', cur: kpi.total_revenue, prev: kpiCompare.total_revenue, money: true },
                    { k: 'ROI (x)', cur: kpi.roi, prev: kpiCompare.roi, decimals: 1 },
                    { k: 'Stunden', cur: kpi.hours_invested, prev: kpiCompare.hours_invested },
                  ].map((row) => {
                    const delta = row.cur - row.prev;
                    const fmt = (v: number) =>
                      (row as { money?: boolean }).money
                        ? formatCurrency(v)
                        : (row as { decimals?: number }).decimals != null
                          ? v.toFixed((row as { decimals: number }).decimals)
                          : formatNumber(v);
                    return (
                      <tr key={row.k} className="border-b border-border">
                        <td className="py-2 pr-4 font-medium text-muted">{row.k}</td>
                        <td className="py-2 pr-4 tabular-nums">{fmt(row.cur)}</td>
                        <td className="py-2 pr-4 tabular-nums">{fmt(row.prev)}</td>
                        <td className={cn('py-2 tabular-nums', delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : '')}>
                          {delta > 0 ? '+' : ''}
                          {(row as { money?: boolean }).money ? formatCurrency(delta) : formatNumber(delta)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget chart */}
      <Card className="border-border bg-card shadow-sm">
        <CardHeader>
          <CardTitle>Budget nach Kanal (Geplant vs. Ist)</CardTitle>
          <p className="text-sm text-muted">Vergleich geplantes und tatsächliches Budget pro Kanal</p>
        </CardHeader>
        <CardContent>
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={budgetByChannel} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-primary-foreground dark:stroke-muted" />
                <XAxis dataKey="channel" tick={{ fontSize: 12 }} className="fill-muted " />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} className="fill-muted " />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: 8, border: '1px solid var(--color-slate-200)' }} />
                <Legend />
                <Bar dataKey="planned" name="Geplant" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Ist" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card shadow-sm">
        <CardHeader>
          <CardTitle>Kostenentwicklung Ads (30 Tage)</CardTitle>
          <p className="text-sm text-muted">Google und LinkedIn Gesamtkosten pro Tag</p>
        </CardHeader>
        <CardContent>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={adCostTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-primary-foreground dark:stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted " />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} className="fill-muted " />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: 8, border: '1px solid var(--color-slate-200)' }} />
                <Line type="monotone" dataKey="cost" name="Kosten" stroke={CHART_COLORS[2]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Revenue timeline */}
      <Card className="border-border bg-card shadow-sm">
        <CardHeader>
          <CardTitle>Umsatzverlauf (monatlich)</CardTitle>
          <p className="text-sm text-muted">Monatlicher Umsatz in CHF</p>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueTimeline} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-primary-foreground dark:stroke-muted" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} className="fill-muted " />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} className="fill-muted " />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ borderRadius: 8, border: '1px solid var(--color-slate-200)' }} />
                <Line type="monotone" dataKey="revenue" name="Umsatz" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ fill: CHART_COLORS[0] }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Channel performance table */}
        <Card className="border-border bg-card shadow-sm">
          <CardHeader>
            <CardTitle>Kanal-Performance</CardTitle>
            <p className="text-sm text-muted">Leads, Conversions, Umsatz, Kosten, ROI und ROAS</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-2 text-left font-medium text-muted">Kanal</th>
                    <th className="pb-2 text-right font-medium text-muted">Leads</th>
                    <th className="pb-2 text-right font-medium text-muted">Conversions</th>
                    <th className="pb-2 text-right font-medium text-muted">Umsatz</th>
                    <th className="pb-2 text-right font-medium text-muted">Kosten</th>
                    <th className="pb-2 text-right font-medium text-muted">ROI</th>
                    <th className="pb-2 text-right font-medium text-muted">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {channelPerf.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-muted">Keine Kanaldaten vorhanden.</td>
                    </tr>
                  ) : (
                    channelPerf.map((row) => (
                      <tr key={row.channel} className="border-b border-border">
                        <td className="py-2 font-medium text-foreground">{row.type}</td>
                        <td className="py-2 text-right text-muted">{formatNumber(row.leads)}</td>
                        <td className="py-2 text-right text-muted">{formatNumber(row.conversions)}</td>
                        <td className="py-2 text-right text-muted">{formatCurrency(row.revenue)}</td>
                        <td className="py-2 text-right text-muted">{formatCurrency(row.cost)}</td>
                        <td className="py-2 text-right">
                          <Badge variant={row.roi >= 2 ? 'success' : 'secondary'}>{row.roi.toFixed(2)}x</Badge>
                        </td>
                        <td className="py-2 text-right">
                          <Badge variant={(row.roas ?? 0) >= 2 ? 'success' : 'secondary'}>{(row.roas ?? 0).toFixed(2)}x</Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Top 5 campaigns by ROI */}
        <Card className="border-border bg-card shadow-sm">
          <CardHeader>
            <CardTitle>Top 5 Kampagnen (nach ROI)</CardTitle>
            <p className="text-sm text-muted">Beste Kampagnen nach Return on Investment</p>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {topCampaigns.length === 0 ? (
                <li className="py-4 text-center text-muted">Keine Kampagnen-Daten.</li>
              ) : (
                topCampaigns.map((c, i) => (
                  <li key={c.name} className="flex items-center justify-between rounded-lg border border-border bg-background/50 px-3 py-2 dark:bg-card-hover/50">
                    <div className="flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-card-hover text-xs font-bold text-foreground dark:bg-primary-hover dark:text-muted">
                        {i + 1}
                      </span>
                      <span className="font-medium text-foreground">{c.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted">{formatCurrency(c.revenue)}</span>
                      <Badge className={cn(statusColors.active)}>{c.roi.toFixed(1)}x ROI</Badge>
                      <Badge variant="secondary">{(c.roas ?? c.roi).toFixed(1)}x ROAS</Badge>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-border bg-card shadow-sm">
          <CardHeader>
            <CardTitle>Top Keywords nach Kosten (Google Ads)</CardTitle>
            <p className="text-sm text-muted">Einfache Keyword-Kostensicht mit CPC/CPA</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-2 text-left font-medium text-muted">Keyword</th>
                    <th className="pb-2 text-right font-medium text-muted">Kosten</th>
                    <th className="pb-2 text-right font-medium text-muted">Klicks</th>
                    <th className="pb-2 text-right font-medium text-muted">Conv.</th>
                    <th className="pb-2 text-right font-medium text-muted">CPC</th>
                    <th className="pb-2 text-right font-medium text-muted">CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {adKeywordCosts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-muted">Keine Keyword-Daten vorhanden.</td>
                    </tr>
                  ) : (
                    adKeywordCosts.map((row) => (
                      <tr key={`${row.campaign_name}-${row.keyword}`} className="border-b border-border">
                        <td className="py-2 text-foreground">{row.keyword}</td>
                        <td className="py-2 text-right text-muted">{formatCurrency(row.cost)}</td>
                        <td className="py-2 text-right text-muted">{formatNumber(row.clicks)}</td>
                        <td className="py-2 text-right text-muted">{formatNumber(row.conversions)}</td>
                        <td className="py-2 text-right text-muted">{formatCurrency(row.cpc)}</td>
                        <td className="py-2 text-right text-muted">{formatCurrency(row.cpa)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
